<template>
    <div
        v-if="editor"
        ref="editorRootRef"
        :class="{'editor--readonly': isReadonly}"
        class="editor"
        @click="onEditorClick"
        @contextmenu="onContextMenu"
    >
        <PortfolioEditorTableContextMenu
            v-if="!isReadonly"
            ref="tableContextMenuRef"
            :add-column-after="addColumnAfter"
            :add-column-before="addColumnBefore"
            :add-row-after="addRowAfter"
            :add-row-before="addRowBefore"
            :delete-column="deleteColumn"
            :delete-row="deleteRow"
            :delete-table="deleteTable"
        />
        <PortfolioEditorFloatingMenu
            v-if="!isReadonly"
            :editor="editor"
            @trigger-image-upload="triggerImageUpload"
            @open-video-dialog="openVideoDialog"
            @insert-divider="insertDivider"
            @insert-table="insertTable"
            @insert-blockquote="insertBlockquote"
            @insert-heading1="insertHeading1"
            @insert-heading2="insertHeading2"
        />
        <EditorContent :editor="editor" />
        <PortfolioEditorBubbleMenu v-if="!isReadonly" :editor="editor" />
        <PortfolioEditorSlashMenu
            v-if="!isReadonly"
            :on-delete-slash-text="deleteSlashText"
            :on-hide="hideSlashMenu"
            :slash-menu-state="slashMenuState"
            @trigger-image-upload="triggerImageUpload"
            @open-video-dialog="openVideoDialog"
            @insert-divider="insertDivider"
            @insert-table="insertTable"
            @insert-blockquote="insertBlockquote"
            @insert-heading1="insertHeading1"
            @insert-heading2="insertHeading2"
        />
        <input v-if="!isReadonly" ref="fileInputRef" accept="image/*" class="editor__file-input" type="file" @change="onImageFileSelected" />

        <VJoyDialog v-if="!isReadonly" ref="videoDialog" cancel-text="Cancel" confirm-text="Insert" @dialog:confirm="onVideoConfirm">
            <template #dialog-header>Insert a video</template>
            <template #dialog-body>
                <VJoyInput v-model="videoUrl" name="video-url" placeholder="https://www.youtube.com/watch?v=... or https://vimeo.com/..." />
            </template>
        </VJoyDialog>
    </div>
</template>

<script lang="ts" setup>
    import {computed, ref} from 'vue';
    import {EditorContent} from '@tiptap/vue-3';
    import {useTranslation} from '#imports';
    import {VJoyDialog, VJoyInput} from '@maltjoy/core-vue';
    import type {EditorJsonNode} from '~/features/portfolio/editor/composable/portfolioEditorItems';
    import {useDocumentListener} from '~/features/portfolio/editor/composable/useDocumentListener';
    import {usePortfolioItemEditor} from '~/features/portfolio/editor/composable/usePortfolioItemEditor';
    import PortfolioEditorBubbleMenu from './PortfolioEditorBubbleMenu.vue';
    import PortfolioEditorFloatingMenu from './PortfolioEditorFloatingMenu.vue';
    import PortfolioEditorSlashMenu from './PortfolioEditorSlashMenu.vue';
    import PortfolioEditorTableContextMenu from './PortfolioEditorTableContextMenu.vue';

    const props = withDefaults(
        defineProps<{
            readonly?: boolean;
        }>(),
        {
            readonly: false,
        },
    );
    const {t} = useTranslation();
    const model = defineModel<EditorJsonNode | null>({default: null});
    const isReadonly = computed(() => props.readonly);
    const editorRootRef = ref<HTMLElement | null>(null);
    const EMPTY_EDITOR_CONTENT: EditorJsonNode = {
        type: 'doc',
        content: [{type: 'paragraph'}],
    };

    const {
        editor,
        fileInputRef,
        videoUrl,
        slashMenuState,
        hideSlashMenu,
        deleteSlashText,
        triggerImageUpload,
        onImageFileSelected,
        insertVideo,
        insertTable,
        addColumnBefore,
        addColumnAfter,
        deleteColumn,
        addRowBefore,
        addRowAfter,
        deleteRow,
        deleteTable,
        insertBlockquote,
        insertDivider,
        insertHeading1,
        insertHeading2,
    } = usePortfolioItemEditor({
        readonly: isReadonly.value,
        content: model.value ?? EMPTY_EDITOR_CONTENT,
        onUpdateContent: (content) => {
            model.value = content;
        },
        placeholder: t('profile.section.portfolio.editor.placeholder'),
    });

    // Video dialog
    const videoDialog = ref<InstanceType<typeof VJoyDialog>>();

    function openVideoDialog() {
        videoUrl.value = '';
        videoDialog.value?.show();
    }

    function onVideoConfirm() {
        insertVideo();
        videoDialog.value?.hide();
    }

    // Table context menu
    const tableContextMenuRef = ref<InstanceType<typeof PortfolioEditorTableContextMenu>>();

    function onEditorClick() {
        if (isReadonly.value) {
            return;
        }
        editor.value?.commands.focus();
    }

    function onContextMenu(event: MouseEvent) {
        if (isReadonly.value) {
            return;
        }
        if (!(event.target as HTMLElement).closest('table')) return;
        event.preventDefault();
        tableContextMenuRef.value?.show(event.clientX, event.clientY);
    }

    function onPageClick(event: MouseEvent) {
        if (isReadonly.value) {
            return;
        }

        const target = event.target as Node | null;
        if (!target) {
            return;
        }

        if (editorRootRef.value?.contains(target)) {
            return;
        }

        const targetElement = target instanceof Element ? target : target.parentElement;
        if (targetElement?.closest('[role="dialog"], [aria-modal="true"], .joy-dialog')) {
            return;
        }

        editor.value?.commands.focus();
    }

    useDocumentListener('click', onPageClick);
</script>

<style lang="scss">
    .resize-cursor {
        cursor: col-resize;
    }

    /* Drag handle appended by tiptap-extension-global-drag-handle */
    .drag-handle {
        --drag-size: 24px;
        position: fixed;
        z-index: 100;
        width: var(--drag-size);
        height: var(--drag-size);
        cursor: grab;
        color: var(--joy-color-secondary-50);
        transition:
            opacity 0.15s,
            background var(--joy-transition-duration-short);

        &::before {
            content: '⠿';
            font-size: var(--drag-size);
            line-height: 1.2;
        }

        &:active {
            cursor: grabbing;
        }

        &.hide {
            opacity: 0;
            pointer-events: none;
        }
    }

    .dragging * {
        cursor: grabbing !important;
    }
</style>

<style lang="scss" scoped>
    .editor {
        position: relative;
        max-width: 720px;
        margin: 0 auto;
        overflow-x: hidden; /* clip table overflow without blocking inner tableWrapper scrollbar */
        padding: var(--joy-core-spacing-8) var(--joy-core-spacing-6) var(--joy-core-spacing-8);

        &__file-input {
            display: none;
        }

        &.editor--readonly {
            padding: 0;
            margin: 0;
            max-width: 672px;

            :deep(.editor__prosemirror) {
                padding-left: 0;
                min-height: 34vh;
            }
        }

        :deep(.editor__prosemirror) {
            outline: none;
            min-height: 60vh;
            padding-left: 80px; /* gutter for the floating + button and drag handle */
            font-size: var(--joy-font-size-primary-400);
            line-height: 1.6;
            color: var(--joy-color-text-body);

            p.is-editor-empty:first-child::before {
                content: attr(data-placeholder);
                float: left;
                color: var(--joy-color-neutral-40);
                pointer-events: none;
                height: 0;
            }

            h1,
            h2,
            h3 {
                font-weight: var(--joy-font-weight-bold);
                line-height: var(--joy-line-height-large);
                margin: var(--joy-core-spacing-4) 0 var(--joy-core-spacing-2);
            }

            h1 {
                font-size: var(--joy-font-size-secondary-400);
            }

            h2 {
                font-size: var(--joy-font-size-secondary-200);
            }

            h3 {
                font-size: var(--joy-font-size-primary-600);
            }

            ul,
            ol {
                padding-left: var(--joy-core-spacing-6);
            }

            blockquote {
                border-left: 3px solid var(--joy-color-neutral-30);
                padding-left: var(--joy-core-spacing-4);
                color: var(--joy-color-neutral-40);
                margin: var(--joy-core-spacing-3) 0;
            }

            hr {
                border: none;
                border-top: 1px solid var(--joy-color-neutral-30);
                margin: var(--joy-core-spacing-6) 0;
                clear: both; /* clear floated images */
            }

            code {
                background: var(--joy-color-neutral-20);
                border-radius: var(--joy-core-radius-2);
                padding: var(--joy-core-spacing-0) var(--joy-core-spacing-1);
                font-family: monospace;
                font-size: var(--joy-font-size-primary-300);
            }

            pre {
                background: var(--joy-color-neutral-20);
                border-radius: var(--joy-core-radius-3);
                padding: var(--joy-core-spacing-4);
                overflow-x: auto;

                code {
                    background: none;
                    padding: 0;
                }
            }

            .tableWrapper {
                overflow-x: auto;
                min-width: 0 !important; /* override TipTap's inline min-width set by column resize plugin */
            }

            table {
                border-collapse: collapse;
                table-layout: fixed;
                width: 100%;
                max-width: 100%;
                min-width: 0 !important;
                margin: var(--joy-core-spacing-4) 0;
                font-size: var(--joy-font-size-primary-400);
            }

            th,
            td {
                position: relative;
                border: 1px solid var(--joy-color-neutral-30);
                padding: var(--joy-core-spacing-2) var(--joy-core-spacing-3);
                text-align: left;
                vertical-align: top;
            }

            th {
                background: var(--joy-color-neutral-20);
                font-weight: var(--joy-font-weight-bold);
            }

            td p,
            th p {
                margin: 0;
            }

            .column-resize-handle {
                position: absolute;
                right: -2px;
                top: 0;
                bottom: 0;
                width: 4px;
                margin: 0;
                padding: 0;
                line-height: 0;
                font-size: 0;
                background: var(--joy-color-secondary-30);
                pointer-events: none;
                z-index: 1;
            }

            .selectedCell {
                background: var(--joy-color-secondary-10);
            }

            div[data-youtube-video] {
                margin: var(--joy-core-spacing-4) 0;
                border-radius: var(--joy-core-radius-3);
                overflow: hidden;
                aspect-ratio: 16 / 9;

                iframe {
                    width: 100%;
                    height: 100%;
                    border: none;
                }
            }
        }
    }
</style>
